/**
 * Comprehensive utilities for CJK (Chinese, Japanese, Korean) text processing
 * Consolidates all CJK-related functions in one place
 */

/**
 * CJK Character ranges:
 * - CJK Unified Ideographs: U+4E00-U+9FFF (Chinese characters/Kanji)
 * - CJK Extension A: U+3400-U+4DBF (Extended Chinese characters)
 * - Hiragana: U+3040-U+309F (Japanese phonetic script)
 * - Katakana: U+30A0-U+30FF (Japanese phonetic script)
 */
import { logger } from "./logger";

const CJK_REGEX = /[\u4e00-\u9fff\u3400-\u4dbf\u3040-\u309f\u30a0-\u30ff]/;

/**
 * Detect if text contains CJK characters
 */
export function containsCJKCharacters(text: string): boolean {
  return CJK_REGEX.test(text);
}

/**
 * Detect if language is CJK based on text content
 * More comprehensive than just checking for characters
 */
export function isCJKLanguage(text: string): boolean {
  if (!text) return false;

  const cjkCharCount = (text.match(new RegExp(CJK_REGEX.source, "g")) || [])
    .length;
  const totalChars = text.replace(/\s+/g, "").length;

  // If more than 30% of non-space characters are CJK, consider it a CJK language
  return totalChars > 0 && cjkCharCount / totalChars > 0.3;
}

/**
 * Check if text requires Japanese-specific processing
 * (higher threshold than general CJK detection)
 */
export function requiresJapaneseProcessing(text: string): boolean {
  if (!text) return false;

  const japaneseCharCount = (
    text.match(/[\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf]/g) || []
  ).length;
  const totalChars = text.length;

  // If more than 40% of characters are Japanese, use advanced processing
  return japaneseCharCount / totalChars > 0.4;
}

/**
 * Determine the type of Japanese character
 */
export function getJapaneseCharType(
  char: string,
): "hiragana" | "katakana" | "kanji" | "punctuation" | "latin" | "number" {
  const codePoint = char.codePointAt(0);
  if (!codePoint) return "punctuation";

  if (codePoint >= 0x3040 && codePoint <= 0x309f) return "hiragana";
  if (codePoint >= 0x30a0 && codePoint <= 0x30ff) return "katakana";
  if (
    (codePoint >= 0x4e00 && codePoint <= 0x9faf) ||
    (codePoint >= 0x3400 && codePoint <= 0x4dbf)
  )
    return "kanji";
  if (
    (codePoint >= 0x30 && codePoint <= 0x39) ||
    (codePoint >= 0xff10 && codePoint <= 0xff19)
  )
    return "number";
  if (
    (codePoint >= 0x41 && codePoint <= 0x5a) ||
    (codePoint >= 0x61 && codePoint <= 0x7a) ||
    (codePoint >= 0xff21 && codePoint <= 0xff5a)
  )
    return "latin";

  return "punctuation";
}

/**
 * Normalize text for CJK languages
 * Removes specific punctuation while preserving CJK characters
 */
export function normalizeCJKText(text: string): string {
  if (!text) return "";

  return (
    text
      .normalize("NFC")
      .toLowerCase()
      // Remove common CJK punctuation but keep the characters
      .replace(/[.,!?;:()[\]{}""''「」『』。、！？；：（）【】〈〉《》]/g, "")
      .trim()
      .replace(/\s+/g, "")
  );
}

/**
 * Tokenize text with CJK language support
 */
export function tokenizeTextWithCJK(text: string): string[] {
  if (!text) return [];

  // For CJK languages, treat the whole text as a single unit
  // since they don't use spaces between words
  if (containsCJKCharacters(text)) {
    logger.debug("[cjk] CJK tokenization: treating as single unit");
    return [text.trim()];
  }

  // For non-CJK languages, split by spaces
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
}

/**
 * Segment CJK text by character types
 * Useful for more granular text processing
 */
export function segmentCJKText(
  text: string,
): Array<{ text: string; type: string; start: number; end: number }> {
  const segments: Array<{
    text: string;
    type: string;
    start: number;
    end: number;
  }> = [];
  let currentSegment = "";
  let currentType = "";
  let segmentStart = 0;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const charType = getJapaneseCharType(char);

    if (charType !== currentType) {
      // End previous segment
      if (currentSegment) {
        segments.push({
          text: currentSegment,
          type: currentType,
          start: segmentStart,
          end: i,
        });
      }

      // Start new segment
      currentSegment = char;
      currentType = charType;
      segmentStart = i;
    } else {
      currentSegment += char;
    }
  }

  // Add final segment
  if (currentSegment) {
    segments.push({
      text: currentSegment,
      type: currentType,
      start: segmentStart,
      end: text.length,
    });
  }

  return segments;
}

/**
 * Find best match for CJK text when exact match fails
 */
export function findCJKFuzzyMatch(
  text: string,
  targetWord: string,
  startPosition: number = 0,
): { start: number; end: number; confidence: number; match: string } | null {
  // Try exact match first
  const exactIndex = text.indexOf(targetWord, startPosition);
  if (exactIndex !== -1) {
    return {
      start: exactIndex,
      end: exactIndex + targetWord.length,
      confidence: 1.0,
      match: targetWord,
    };
  }

  // For single characters, try exact match only
  if (targetWord.length === 1) {
    return null;
  }

  // Try to find partial matches using character type segmentation
  const textSegments = segmentCJKText(text.substring(startPosition));
  const targetSegments = segmentCJKText(targetWord);

  // Look for sequence of segments that best matches the target
  for (let i = 0; i <= textSegments.length - targetSegments.length; i++) {
    let matchScore = 0;

    for (let j = 0; j < targetSegments.length; j++) {
      const textSeg = textSegments[i + j];
      const targetSeg = targetSegments[j];

      if (textSeg && targetSeg) {
        if (textSeg.text === targetSeg.text) {
          matchScore += 1.0;
        } else if (textSeg.type === targetSeg.type) {
          // Partial match by character type
          matchScore += 0.5;
        }
      }
    }

    const confidence = matchScore / targetSegments.length;

    if (confidence >= 0.6) {
      // Minimum confidence threshold
      const matchStart = startPosition + textSegments[i].start;
      const matchEnd =
        startPosition + textSegments[i + targetSegments.length - 1].end;
      const matchText = text.substring(matchStart, matchEnd);

      return {
        start: matchStart,
        end: matchEnd,
        confidence,
        match: matchText,
      };
    }
  }

  return null;
}

/**
 * Enhanced word mapping for CJK languages
 * Maps word boundaries to text positions using CJK-aware algorithms
 */
export function mapCJKWordBoundaries(
  text: string,
  wordBoundaries: Array<{ word: string; offset: number; duration: number }>,
): Array<{
  start: number;
  end: number;
  wordIndex: number;
  confidence: number;
}> {
  const mappings: Array<{
    start: number;
    end: number;
    wordIndex: number;
    confidence: number;
  }> = [];
  let currentNormPosition = 0; // позиция внутри нормализованной строки

  // ВАЖНО: ранее мы нормализовали весь текст и возвращали индексы по нормализованной строке.
  // Это приводило к смещению после удаления пунктуации (например "。") — подсветка сдвигалась и
  // визуально подсвечивалось следующее слово. Теперь строим отображение индексов.

  // Строим нормализованный текст и карту соответствия индекс -> оригинальный индекс
  const normalizedIndexMap: number[] = []; // normalizedIndex -> originalIndex
  let normalizedBuilder = "";
  const pushChar = (origChar: string, origIndex: number): void => {
    // Удаляем только ту пунктуацию, которую normalizCJKText удаляет.
    if (
      /[.,!?;:()[\]{}""''「」『』。、！？；：（）【】〈〉《》]/.test(origChar)
    ) {
      return; // пропускаем, не добавляя в нормализованный
    }
    // Приводим к нижнему регистру (как normalizeCJKText) и добавляем без пробелов
    const lower = origChar.toLowerCase();
    if (/\s/.test(lower)) return; // убираем пробелы
    normalizedBuilder += lower;
    normalizedIndexMap.push(origIndex);
  };

  for (let i = 0; i < text.length; i++) {
    pushChar(text[i], i);
  }

  const normalizedText = normalizedBuilder;

  logger.debug("[cjk] Advanced CJK mapping started", {
    textLength: text.length,
    normalizedLength: normalizedText.length,
    wordsCount: wordBoundaries.length,
  });

  for (let i = 0; i < wordBoundaries.length; i++) {
    const boundary = wordBoundaries[i];
    const normalizedWord = normalizeCJKText(boundary.word);

    if (!normalizedWord) {
      logger.warn(
        `[cjk] Empty normalized word for boundary '${boundary.word}'`,
      );
      continue;
    }

    // Пытаемся найти точное совпадение в нормализованном тексте
    const normIndex = normalizedText.indexOf(
      normalizedWord,
      currentNormPosition,
    );

    const convertNormRangeToOriginal = (
      startNorm: number,
      endNorm: number,
    ): { start: number; end: number } => {
      // endNorm не включительно
      const startOrig = normalizedIndexMap[startNorm];
      const endOrigInclusive = normalizedIndexMap[endNorm - 1];
      return { start: startOrig, end: endOrigInclusive + 1 };
    };

    if (normIndex !== -1) {
      const normEnd = normIndex + normalizedWord.length;
      const { start, end } = convertNormRangeToOriginal(normIndex, normEnd);
      mappings.push({
        start,
        end,
        wordIndex: i,
        confidence: 1.0,
      });
      logger.debug(
        `[cjk] Exact match "${boundary.word}" at norm ${normIndex}-${normEnd} -> orig ${start}-${end}`,
      );
      currentNormPosition = normEnd;
      continue;
    }

    // Fallback: пробуем fuzzy на нормализованной строке
    const fuzzyMatch = findCJKFuzzyMatch(
      normalizedText,
      normalizedWord,
      currentNormPosition,
    );

    if (fuzzyMatch) {
      const { start, end } = convertNormRangeToOriginal(
        fuzzyMatch.start,
        fuzzyMatch.end,
      );
      mappings.push({
        start,
        end,
        wordIndex: i,
        confidence: fuzzyMatch.confidence,
      });
      logger.debug(
        `[cjk] Fuzzy match "${boundary.word}" -> norm ${fuzzyMatch.start}-${fuzzyMatch.end} -> orig ${start}-${end} (${fuzzyMatch.confidence.toFixed(2)})`,
      );
      currentNormPosition = fuzzyMatch.end;
    } else {
      logger.warn(`[cjk] Failed to map "${boundary.word}"`);
    }
  }

  const successRate = mappings.length / wordBoundaries.length;
  logger.debug(
    `[cjk] CJK mapping completed: ${mappings.length}/${wordBoundaries.length} (${(successRate * 100).toFixed(1)}%)`,
  );

  return mappings;
}

/**
 * Check if text primarily consists of CJK characters
 * More strict than containsCJKCharacters
 */
export function isPrimarilyCJK(text: string): boolean {
  if (!text) return false;

  const nonSpaceText = text.replace(/\s+/g, "");
  const cjkChars = nonSpaceText.match(new RegExp(CJK_REGEX.source, "g")) || [];

  return cjkChars.length / nonSpaceText.length > 0.7; // 70% threshold
}
